import { Form, Formik, Field, ErrorMessage } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import * as Yup from "yup";
import { getColdLeadAsync, updateColdLeadAsync, checkContactNumberExistsAsync } from "../redux";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { regexValidation } from "../../../../_metronic/_helpers";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";

const ViewCandidatesMaster = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { coldLeadDetails } = useSelector(
    (state) => ({
      coldLeadDetails: state.candidates_master.selectedColdLead,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (id) {
      dispatch(getColdLeadAsync(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const redirectBack = () => {
    history.push("/candidates-master");
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .trim()
      .required("First Name is required")
      .min(2, "First Name must be at least 2 characters")
      .max(100, "First Name must not exceed 100 characters"),
    last_name: Yup.string()
      .trim()
      .required("Last Name is required")
      .min(2, "Last Name must be at least 2 characters")
      .max(100, "Last Name must not exceed 100 characters"),
    email: Yup.string()
      .trim()
      .nullable()
      .notRequired()
      .test("email-format", "Enter valid email", function (value) {
        if (!value || value.length === 0) {
          return true; // Email is optional, so empty is valid
        }
        return regexValidation.EMAIL_REGEX.test(value);
      }),
    contactNumber: Yup.string()
      .trim()
      .required("Contact Number is required")
      .matches(regexValidation.CONTACT_NUMBER_REGEX, "Contact Number is not valid"),
    technology: Yup.string()
      .trim()
      .notRequired(),
    experience_years: Yup.number()
      .nullable()
      .notRequired()
      .min(0, "Experience years must be positive")
      .max(50, "Experience years must be less than 50"),
    education: Yup.string()
      .trim()
      .notRequired()
      .max(200, "Education must not exceed 200 characters"),
    current_location: Yup.string()
      .trim()
      .notRequired()
      .max(100, "Current Location must not exceed 100 characters"),
    hiring_location: Yup.string()
      .trim()
      .notRequired()
      .max(100, "Hiring Location must not exceed 100 characters"),
    resume_link: Yup.string()
      .trim()
      .nullable()
      .notRequired()
      .url("Resume link must be a valid URL"),
    job_position: Yup.string()
      .trim()
      .notRequired()
      .max(200, "Job Position must not exceed 200 characters"),
    batchName: Yup.string()
      .trim()
      .notRequired()
      .max(100, "Batch must not exceed 100 characters"),
  });

  // Helper function to convert technology array to comma-separated string
  const technologyArrayToString = (techArray) => {
    if (!techArray || !Array.isArray(techArray)) return "";
    return techArray.join(", ");
  };

  // Helper function to convert comma-separated string to technology array
  const technologyStringToArray = (techString) => {
    if (!techString || typeof techString !== "string") return [];
    return techString.split(",").map(item => item.trim()).filter(Boolean);
  };

  const initialValues = {
    first_name: coldLeadDetails?.first_name || "",
    last_name: coldLeadDetails?.last_name || "",
    email: coldLeadDetails?.email || "",
    contactNumber: coldLeadDetails?.contactNumber || "",
    technology: technologyArrayToString(coldLeadDetails?.technology),
    experience_years: coldLeadDetails?.experience_years || "",
    education: coldLeadDetails?.education || "",
    current_location: coldLeadDetails?.current_location || coldLeadDetails?.country || "",
    hiring_location: coldLeadDetails?.hiring_location || coldLeadDetails?.city || "",
    resume_link: coldLeadDetails?.resume_link || "",
    job_position: coldLeadDetails?.job_position || "",
    batchName: coldLeadDetails?.batchName || coldLeadDetails?.batch_name || "",
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setIsSubmitting(true);
    try {
      // Trim contact number for comparison
      const trimmedContactNumber = values.contactNumber?.trim() || "";
      const originalContactNumber = coldLeadDetails?.contactNumber?.trim() || "";
      
      // Check for duplicate contact number ONLY if the number has changed
      if (trimmedContactNumber !== originalContactNumber) {
        const duplicateCheck = await dispatch(checkContactNumberExistsAsync(trimmedContactNumber, id));
        
        // If duplicate exists, show error and stop
        if (duplicateCheck && duplicateCheck.exists === true) {
          setFieldError("contactNumber", "This number already exists");
          setIsSubmitting(false);
          setSubmitting(false);
          return;
        }
      }

      // Prepare data with trimmed values
      const submitData = {
        first_name: (values.first_name || "").trim(),
        last_name: (values.last_name || "").trim(),
        email: (values.email || "").trim() || undefined,
        contactNumber: trimmedContactNumber,
        technology: technologyStringToArray(values.technology || ""),
        experience_years: values.experience_years ? parseFloat(values.experience_years) : undefined,
        education: (values.education || "").trim() || undefined,
        current_location: (values.current_location || "").trim() || undefined,
        hiring_location: (values.hiring_location || "").trim() || undefined,
        resume_link: (values.resume_link || "").trim() || undefined,
        job_position: (values.job_position || "").trim() || undefined,
        batchName: (values.batchName || "").trim() || undefined,
      };

      // Remove undefined and empty values
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === undefined || submitData[key] === "" || 
            (Array.isArray(submitData[key]) && submitData[key].length === 0)) {
          delete submitData[key];
        }
      });
      
      const result = await dispatch(updateColdLeadAsync(id, submitData));
      
      if (result && result.success) {
        setTimeout(() => {
          redirectBack();
        }, 1000);
      } else {
        // Handle errors
        if (result && result.isDuplicate) {
          setFieldError("contactNumber", "This number already exists");
        } else if (result && result.error) {
          // Error snackbar is already shown by updateColdLeadAsync
        }
      }
    } catch (error) {
      console.error("Exception during update:", error);
      dispatch(showSuccessSnackbar("error", "Error while updating candidate", 3000));
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const getInputClasses = (fieldname, touched, errors) => {
    if (touched && errors) {
      return "form-control re_inputRouded is-invalid";
    }
    if (touched && !errors) {
      return "form-control re_inputRouded is-valid";
    }
    return "form-control re_inputRouded";
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, touched, errors, isSubmitting: formikSubmitting }) => (
        <Row className="justify-content-center">
          <Col lg={11}>
            <Card>
              <CardHeader title="Edit Candidates Master" />
              <CardBody>
                <Form>
                  <Row>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">First Name <span className="text-danger">*</span></label>
                        <Field
                          className={getInputClasses("first_name", touched.first_name, errors.first_name)}
                          placeholder="First Name"
                          type="text"
                          name="first_name"
                        />
                        <ErrorMessage name="first_name" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Last Name <span className="text-danger">*</span></label>
                        <Field
                          className={getInputClasses("last_name", touched.last_name, errors.last_name)}
                          placeholder="Last Name"
                          type="text"
                          name="last_name"
                        />
                        <ErrorMessage name="last_name" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Email</label>
                        <Field
                          className={getInputClasses("email", touched.email, errors.email)}
                          placeholder="Email"
                          type="email"
                          name="email"
                        />
                        <ErrorMessage name="email" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Contact Number <span className="text-danger">*</span></label>
                        <Field
                          className={getInputClasses("contactNumber", touched.contactNumber, errors.contactNumber)}
                          placeholder="Contact Number"
                          type="text"
                          name="contactNumber"
                        />
                        <ErrorMessage name="contactNumber" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Batch</label>
                        <Field
                          className={getInputClasses("batchName", touched.batchName, errors.batchName)}
                          placeholder="Batch"
                          type="text"
                          name="batchName"
                        />
                        <ErrorMessage name="batchName" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Experience (Years)</label>
                        <Field
                          className={getInputClasses("experience_years", touched.experience_years, errors.experience_years)}
                          placeholder="Experience in years (e.g., 5.4)"
                          type="number"
                          step="0.1"
                          min="0"
                          max="50"
                          name="experience_years"
                        />
                        <ErrorMessage name="experience_years" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Technology</label>
                        <Field
                          className={getInputClasses("technology", touched.technology, errors.technology)}
                          placeholder="Technology (comma-separated, e.g., MERN, Android)"
                          type="text"
                          name="technology"
                        />
                        <small className="form-text text-muted">Enter technologies separated by commas</small>
                        <ErrorMessage name="technology" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Job Position</label>
                        <Field
                          className={getInputClasses("job_position", touched.job_position, errors.job_position)}
                          placeholder="Job Position"
                          type="text"
                          name="job_position"
                        />
                        <ErrorMessage name="job_position" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Education</label>
                        <Field
                          className={getInputClasses("education", touched.education, errors.education)}
                          placeholder="Education (e.g., MCA, B.E)"
                          type="text"
                          name="education"
                        />
                        <ErrorMessage name="education" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Hiring Location</label>
                        <Field
                          className={getInputClasses("hiring_location", touched.hiring_location, errors.hiring_location)}
                          placeholder="Hiring Location"
                          type="text"
                          name="hiring_location"
                        />
                        <ErrorMessage name="hiring_location" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Current Location</label>
                        <Field
                          className={getInputClasses("current_location", touched.current_location, errors.current_location)}
                          placeholder="Current Location"
                          type="text"
                          name="current_location"
                        />
                        <ErrorMessage name="current_location" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-group">
                        <label className="form-label">Resume Link</label>
                        <Field
                          className={getInputClasses("resume_link", touched.resume_link, errors.resume_link)}
                          placeholder="Resume URL"
                          type="url"
                          name="resume_link"
                        />
                        <ErrorMessage name="resume_link" component="div" className="invalid-feedback" />
                      </div>
                    </Col>
                  </Row>
                  <div className="text-center">
                    <Button
                      variant="blue"
                      className="px-5 defpddng me-3 mr-3"
                      size="lg"
                      type="submit"
                      disabled={isSubmitting || formikSubmitting}
                    >
                      {isSubmitting || formikSubmitting ? "Saving..." : "Save"}
                    </Button>
                    
                    <Button
                      variant="secondary"
                      className="px-5 defpddng"
                      size="lg"
                      onClick={redirectBack}
                      disabled={isSubmitting || formikSubmitting}
                    >
                      Close
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Formik>
  );
};

export default ViewCandidatesMaster;


